/**
 * 딜 채팅 API — Slack 연동 스레드/메시지 + UI 채팅(첨부 포함).
 *
 * ChatThread/ChatMessage 타입은 여기가 소유 — deal-api.ts(DealFull)가 import.
 */

import { API_BASE, authHeaders, handleUnauthorized, getJson, parseResponse } from './http';
import {
  DealChatResponseSchema, ChatMessagePageSchema,
  type ChatThread, type ChatMessage, type DealFullThread,
  type ChatMessagePage, type DealChatResponse,
} from './schemas';

// 타입 정본 = shared z.infer (schemas.ts 관문) — 손 정의 interface 제거(2026-07-12).
// attachments도 unknown이 아니라 ChatFileAttachment[]|null 로 정확해짐.
export type { ChatThread, ChatMessage, DealFullThread, ChatMessagePage, DealChatResponse };

// ─── UI 채팅 (Step 4) ────────────────────────────────────────────────
// POST /api/admin/deal/:id/chat — LLM 호출 후 user+assistant 메시지 둘 다 반환.

/**
 * UI 채팅 첨부 — 이미지 (Ctrl+V paste/file input) 또는 Excel/CSV (file input).
 *
 *   이미지 (image/*):
 *     - Ctrl+V paste 또는 [📎] 버튼으로 선택.
 *     - previewUrl 채워서 썸네일 미리보기.
 *     - backend 에서 multimodal LLM (Vision) 으로 전달.
 *
 *   Excel/CSV (xlsx/xls/csv):
 *     - [📎] 버튼으로 선택 (paste 안 함).
 *     - previewUrl '' — UI 는 📊 아이콘 + 파일명 카드.
 *     - backend 에서 parseExcel → markdown table → LLM user message 에 prepend.
 *
 *   다른 mime (pdf 등) 은 다음 라운드.
 */
export interface ChatAttachmentFile {
  file: File;
  /** 이미지 미리보기용 data URL (FileReader). Excel/CSV 는 빈 string. */
  previewUrl: string;
}

export async function postDealChat(
  id:      string | number,
  content: string,
  attachments: ChatAttachmentFile[] = [],         // 7-4: 첨부
): Promise<DealChatResponse> {
  // LLM 응답이 10-30초 걸리므로 명시적 timeout 300초.
  const url = `${API_BASE}/api/admin/deal/${encodeURIComponent(String(id))}/chat`;
  const startedAt = Date.now();

  // 첨부 있으면 multipart, 없으면 JSON
  const useMultipart = attachments.length > 0;
  let fetchOpts: RequestInit;

  if (useMultipart) {
    const form = new FormData();
    form.append('content', content);
    for (const att of attachments) {
      form.append('files', att.file, att.file.name);
    }
    // multipart 시 Content-Type 헤더 명시 X — 브라우저가 boundary 자동
    fetchOpts = {
      method:  'POST',
      headers: authHeaders(),
      body:    form,
      signal:  AbortSignal.timeout(300_000),
    };
  } else {
    fetchOpts = {
      method:  'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body:    JSON.stringify({ content }),
      signal:  AbortSignal.timeout(300_000),
    };
  }

  try {
    const res = await fetch(url, fetchOpts);

    if (res.status === 401) {
      handleUnauthorized();
      throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(
        `POST chat HTTP ${res.status} ${res.statusText} after ${Date.now() - startedAt}ms` +
        (text ? `: ${text.slice(0, 300)}` : ''),
      );
      (err as Error & { metadata?: unknown }).metadata = {
        phase: 'http_error',
        status: res.status,
        statusText: res.statusText,
        duration_ms: Date.now() - startedAt,
        body_excerpt: text.slice(0, 300),
      };
      throw err;
    }

    return parseResponse(await res.json(), DealChatResponseSchema, 'POST /api/admin/deal/:id/chat');
  } catch (err) {
    const e = err as Error & { name?: string; cause?: Error; code?: string };
    const wrapped = new Error(
      `채팅 전송 실패 (${e.name ?? 'Error'}) after ${Date.now() - startedAt}ms: ${e.message}`,
    );
    (wrapped as Error & { cause?: unknown }).cause = e;
    (wrapped as Error & { metadata?: unknown }).metadata = {
      phase:        (e as Error & { metadata?: { phase?: string } }).metadata?.phase ?? 'fetch',
      duration_ms:  Date.now() - startedAt,
      err_name:     e.name,
      err_code:     e.code,
      cause_message: e.cause?.message,
      cause_name:    e.cause?.name,
    };
    throw wrapped;
  }
}

// ─── Chat API (deal 의 thread 무한 스크롤) ───────────────────────────
// ChatMessagePage 타입은 shared 정본 (위 re-export).

/** GET /api/admin/chat/thread/:thread_id/messages?before=&limit= */
export async function fetchChatMessagesPage(
  threadId: string,
  opts: { before?: string; limit?: number } = {},
): Promise<ChatMessagePage> {
  const qs = new URLSearchParams();
  if (opts.before) qs.append('before', opts.before);
  qs.append('limit', String(opts.limit ?? 30));
  return getJson<ChatMessagePage>(
    `/api/admin/chat/thread/${encodeURIComponent(threadId)}/messages?${qs.toString()}`,
    { schema: ChatMessagePageSchema },
  );
}
